import { useEffect, useMemo, useState } from 'react'

import {
  Checkbox,
  Modal,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core'
import { useForm } from '@mantine/form'

import { useMutation } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  CREATE_CUSTOM_FIELD,
  UPDATE_CUSTOM_FIELD,
} from 'src/graphql/customFields.graphql'

import CustomFieldOptionsManager from './CustomFieldOptionsManager'
import {
  canBeSearchable,
  generateKeyFromLabel,
  isValidLabelForKey,
  isValidLabelForValue,
  requiresOptions,
} from './customFieldsUtils'

interface CustomFieldModalProps {
  customField?: any | null
  isEdit: boolean
  opened: boolean
  onClose: () => void
  onSuccess: () => void
}

const FIELD_TYPE_OPTIONS = [
  { value: 'STRING', label: 'Text' },
  { value: 'BOOLEAN', label: 'Yes/No' },
  { value: 'DATE', label: 'Date' },
  { value: 'SINGLE_SELECT', label: 'Single Select' },
  { value: 'MULTI_SELECT', label: 'Multi Select' },
]

const CustomFieldModal = ({
  customField,
  isEdit,
  opened,
  onClose,
  onSuccess,
}: CustomFieldModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm({
    initialValues: {
      label: '',
      fieldType: '',
      isRequired: false,
      requiredForStages: [],
      isSearchable: false,
      options: [],
    },
    validate: {
      label: (value) => {
        if (!value.trim()) {
          return 'Label is required'
        }
        if (!isValidLabelForKey(value)) {
          return 'Label must contain at least one letter or number'
        }
        return null
      },
      fieldType: (value) => (value ? null : 'Field type is required'),
      options: (value, values) => {
        if (requiresOptions(values.fieldType)) {
          if (!value || value.length === 0) {
            return 'At least one option is required for select fields'
          }
          const hasEmptyLabels = value.some((opt) => !opt.label.trim())
          if (hasEmptyLabels) {
            return 'All options must have a label'
          }
          const hasInvalidValues = value.some(
            (opt) => opt.label.trim() && !isValidLabelForValue(opt.label)
          )
          if (hasInvalidValues) {
            return 'All option labels must contain at least one letter or number'
          }
        }
        return null
      },
    },
  })

  const [createCustomField] = useMutation(CREATE_CUSTOM_FIELD, {
    onCompleted: () => {
      toast('Custom field created successfully', 'success')
      onSuccess()
      onClose()
      form.reset()
      setIsSubmitting(false)
    },
    onError: (error) => {
      toast(error.message || 'Error creating custom field', 'error')
      setIsSubmitting(false)
    },
  })

  const [updateCustomField] = useMutation(UPDATE_CUSTOM_FIELD, {
    onCompleted: () => {
      toast('Custom field updated successfully', 'success')
      onSuccess()
      onClose()
      setIsSubmitting(false)
    },
    onError: (error) => {
      toast(error.message || 'Error updating custom field', 'error')
      setIsSubmitting(false)
    },
  })

  // Populate form when editing
  useEffect(() => {
    if (isEdit && customField && opened) {
      form.setValues({
        label: customField.label,
        fieldType: customField.fieldType,
        isRequired: customField.isRequired,
        requiredForStages: customField.requiredForStages || [],
        isSearchable: customField.isSearchable,
        options: customField.options.map((opt, idx) => ({
          id: opt.optionId || `temp-${idx}`,
          label: opt.label,
          value: opt.value,
          sortOrder: opt.sortOrder,
        })),
      })
    } else if (!isEdit && opened) {
      form.reset()
    }
  }, [customField, isEdit, opened])

  // Auto-disable searchable for non-searchable types
  useEffect(() => {
    if (!canBeSearchable(form.values.fieldType) && form.values.isSearchable) {
      form.setFieldValue('isSearchable', false)
    }
  }, [form.values.fieldType])

  const generatedKey = useMemo(
    () => generateKeyFromLabel(form.values.label),
    [form.values.label]
  )

  const showOptions = requiresOptions(form.values.fieldType)
  const searchableEnabled = canBeSearchable(form.values.fieldType)

  const handleSubmit = async (values) => {
    setIsSubmitting(true)

    if (isEdit) {
      // Update mutation - only send allowed fields
      const input: any = {
        label: values.label,
        isRequired: values.isRequired,
        requiredForStages: values.requiredForStages,
        isSearchable: values.isSearchable,
      }

      if (showOptions) {
        input.options = values.options.map((opt) => ({
          label: opt.label,
          value: opt.value,
          sortOrder: opt.sortOrder,
        }))
      }

      await updateCustomField({
        variables: {
          customFieldId: customField.customFieldId,
          input,
        },
      })
    } else {
      // Create mutation
      const input: any = {
        key: generatedKey,
        label: values.label,
        fieldType: values.fieldType,
        isRequired: values.isRequired,
        requiredForStages: values.requiredForStages,
        isSearchable: values.isSearchable,
      }

      if (showOptions) {
        input.options = values.options.map((opt) => ({
          label: opt.label,
          value: opt.value,
          sortOrder: opt.sortOrder,
        }))
      }

      await createCustomField({
        variables: { input },
      })
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEdit ? 'Edit Custom Field' : 'Create Custom Field'}
      size="lg"
    >
      <form onSubmit={form.onSubmit(handleSubmit)} className="flex flex-col gap-4">
        <TextInput
          label="Label"
          placeholder="e.g., Driver Type"
          description={
            form.values.label.length > 30
              ? `${form.values.label.length}/100 characters - Consider using a shorter label for better display`
              : 'Keep labels concise for better UI display'
          }
          maxLength={100}
          required
          {...form.getInputProps('label')}
        />

        {!isEdit && generatedKey && (
          <div className="rounded-md bg-doubleNickel-gray-50 p-3">
            <div className="text-xs font-medium text-doubleNickel-gray-600">
              Generated Key (read-only)
            </div>
            <div className="font-mono text-sm text-doubleNickel-gray-700">
              {generatedKey}
            </div>
          </div>
        )}

        {isEdit && (
          <div className="rounded-md bg-doubleNickel-gray-50 p-3">
            <div className="text-xs font-medium text-doubleNickel-gray-600">
              Key (immutable)
            </div>
            <div className="font-mono text-sm text-doubleNickel-gray-700">
              {customField.key}
            </div>
          </div>
        )}

        <Select
          label="Field Type"
          placeholder="Select field type"
          required
          data={FIELD_TYPE_OPTIONS}
          disabled={isEdit}
          {...form.getInputProps('fieldType')}
        />

        {isEdit && (
          <div className="text-xs text-doubleNickel-gray-500">
            Field type cannot be changed after creation
          </div>
        )}

        <Tooltip
          label="Only Boolean, Date, and Select field types can be searchable"
          disabled={searchableEnabled}
        >
          <div>
            <Switch
              label="Searchable"
              description="This field can be used in search filters"
              disabled={!searchableEnabled}
              {...form.getInputProps('isSearchable', { type: 'checkbox' })}
            />
          </div>
        </Tooltip>

        <Switch
          label="Required"
          description="Employees must fill this field"
          {...form.getInputProps('isRequired', { type: 'checkbox' })}
          onChange={(event) => {
            const newValue = event.currentTarget.checked
            form.setFieldValue('isRequired', newValue)
            // If unchecking Required, clear all stage selections
            if (!newValue) {
              form.setFieldValue('requiredForStages', [])
            }
          }}
        />

        {/* Stage-specific required configuration - only show when Required is enabled */}
        {form.values.isRequired && (
          <div>
            <Text size="sm" fw={500} mb="xs">
              Required for Stages
            </Text>
            <Text size="xs" c="dimmed" mb="md">
              Select which applicant stages require this custom field to be filled
            </Text>

            <Stack gap="xs">
              <Checkbox
                label="Hired"
                checked={form.values.requiredForStages?.includes('HIRED') || false}
                onChange={(e) => {
                  const stages = form.values.requiredForStages || []
                  if (e.currentTarget.checked) {
                    form.setFieldValue('requiredForStages', [...stages, 'HIRED'])
                  } else {
                    form.setFieldValue(
                      'requiredForStages',
                      stages.filter((s) => s !== 'HIRED')
                    )
                  }
                }}
              />

              <Checkbox
                label="Disqualified"
                checked={
                  form.values.requiredForStages?.includes('DISQUALIFIED') || false
                }
                onChange={(e) => {
                  const stages = form.values.requiredForStages || []
                  if (e.currentTarget.checked) {
                    form.setFieldValue('requiredForStages', [
                      ...stages,
                      'DISQUALIFIED',
                    ])
                  } else {
                    form.setFieldValue(
                      'requiredForStages',
                      stages.filter((s) => s !== 'DISQUALIFIED')
                    )
                  }
                }}
              />

              <Checkbox
                label="Terminated"
                checked={
                  form.values.requiredForStages?.includes('TERMINATED') || false
                }
                onChange={(e) => {
                  const stages = form.values.requiredForStages || []
                  if (e.currentTarget.checked) {
                    form.setFieldValue('requiredForStages', [
                      ...stages,
                      'TERMINATED',
                    ])
                  } else {
                    form.setFieldValue(
                      'requiredForStages',
                      stages.filter((s) => s !== 'TERMINATED')
                    )
                  }
                }}
              />

              <Checkbox
                label="Not Interested"
                checked={
                  form.values.requiredForStages?.includes('NOT_INTERESTED') ||
                  false
                }
                onChange={(e) => {
                  const stages = form.values.requiredForStages || []
                  if (e.currentTarget.checked) {
                    form.setFieldValue('requiredForStages', [
                      ...stages,
                      'NOT_INTERESTED',
                    ])
                  } else {
                    form.setFieldValue(
                      'requiredForStages',
                      stages.filter((s) => s !== 'NOT_INTERESTED')
                    )
                  }
                }}
              />

              <Checkbox
                label="Prospect"
                checked={
                  form.values.requiredForStages?.includes('PROSPECT') || false
                }
                onChange={(e) => {
                  const stages = form.values.requiredForStages || []
                  if (e.currentTarget.checked) {
                    form.setFieldValue('requiredForStages', [...stages, 'PROSPECT'])
                  } else {
                    form.setFieldValue(
                      'requiredForStages',
                      stages.filter((s) => s !== 'PROSPECT')
                    )
                  }
                }}
              />
            </Stack>
          </div>
        )}

        {showOptions && (
          <CustomFieldOptionsManager
            options={form.values.options}
            onChange={(options) => form.setFieldValue('options', options)}
            error={form.errors.options as string}
          />
        )}

        <div className="mt-4 flex justify-end gap-2">
          <DNButton variant="secondary" onClick={onClose} type="button">
            Cancel
          </DNButton>
          <DNButton
            variant="primary"
            type="submit"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {isEdit ? 'Update' : 'Create'}
          </DNButton>
        </div>
      </form>
    </Modal>
  )
}

export default CustomFieldModal
